import { Close } from "@mui/icons-material";
import { Button, CircularProgress, IconButton, Stack, styled, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router";
import { MeetingTimer } from "src/components";
import { Links, MeetingType } from "src/constants";
import { useBreakpoints, useSignalR } from "src/hooks";
import { OneOnOneMeetingNotificationDto, TeamMeetingNotificationDto, useJoinMeeting } from "src/http";

const MeetingNotificationContainer = styled(Stack)`
  background-color: #ceedff;
  border-radius: 8px;
  padding: 10px 20px;
  justify-content: space-between;
`;

interface Props {
  meeting: OneOnOneMeetingNotificationDto | TeamMeetingNotificationDto;
  onDismiss: () => void;
}

const MeetingNotification = (props: Props) => {
  const { meeting, onDismiss } = props;
  const { mutate: joinMeeting, isLoading: isJoiningMeeting } = useJoinMeeting();
  const { isMdUp } = useBreakpoints();
  const navigate = useNavigate();
  const signalR = useSignalR();

  return (
    <MeetingNotificationContainer direction="row" spacing={isMdUp ? 1 : 0.5}>
      <Stack>
        <Typography variant="body2" component="span" sx={{ whiteSpace: "nowrap" }}>
          {meeting.type === MeetingType.OneOnOne ? `1:1 Meeting` : meeting.title}
        </Typography>
        <Typography component="span" sx={{ whiteSpace: "nowrap" }}>
          {meeting.type === MeetingType.OneOnOne
            ? `${meeting.leadUser.displayName} & ${meeting.otherUser.displayName} meeting is in progress.`
            : `${meeting.team.name} meeting is in progress.`}
        </Typography>
        <Typography variant="caption">
          {"Running time "}
          <MeetingTimer
            startDateTimeUtc={DateTime.fromISO(meeting.currentInstance!.startedAtDateTimeUtc, { zone: "utc" })}
          />
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{
        alignItems: "center"
      }}>
        {isMdUp && (
          <Button
            variant="outlined"
            disabled={isJoiningMeeting}
            endIcon={isJoiningMeeting ? <CircularProgress size={14} sx={{ color: "grey.400" }} /> : null}
            onClick={() => {
              joinMeeting(
                {
                  meetingGuid: props.meeting.guid,
                },
                {
                  onSuccess: () => {
                    signalR.sendRefetchMeetingListItem(props.meeting.guid);
                    signalR.sendRefetchMeetingParticipants(props.meeting.guid);
                    navigate(Links.Meeting(meeting.guid));
                  },
                },
              );
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            Join Meeting
          </Button>
        )}
        <IconButton size="small" onClick={onDismiss}>
          <Close />
        </IconButton>
      </Stack>
    </MeetingNotificationContainer>
  );
};

export { MeetingNotification };
